/**
 * Exam type multi-select component.
 *
 * A reusable component for selecting multiple exam types using toggle buttons.
 * Used in admin forms (questions, titles, introductions).
 *
 * @module ExamTypeMultiSelect
 */

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { EXAM_TYPES } from '@/lib/constants';

export interface ExamTypeMultiSelectProps {
  readonly selectedTypes: string[];
  readonly onChange: (updatedTypes: string[]) => void;
  readonly label?: string;
  readonly className?: string;
  readonly size?: 'sm' | 'default' | 'lg';
  readonly htmlId?: string;
}

export function ExamTypeMultiSelect({
  selectedTypes,
  onChange,
  label,
  className,
  size = 'sm',
  htmlId,
}: Readonly<ExamTypeMultiSelectProps>) {
  const handleToggle = (typeValue: string) => {
    if (selectedTypes.includes(typeValue)) {
      onChange(selectedTypes.filter((t) => t !== typeValue));
    } else {
      onChange([...selectedTypes, typeValue]);
    }
  };

  return (
    <div className={className}>
      {label && (
        <Label htmlFor={htmlId} className="font-ui mb-2">
          {label}
        </Label>
      )}
      <div className="flex gap-2 mt-2">
        {EXAM_TYPES.map((type) => (
          <Button
            key={type.value}
            variant={selectedTypes.includes(type.value) ? 'default' : 'outline'}
            size={size}
            type="button"
            onClick={() => handleToggle(type.value)}
          >
            <span className="font-ui">{type.label}</span>
          </Button>
        ))}
      </div>
    </div>
  );
}
